import { Button, Form, Spin, notification } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { dataForOvertimeUsersTable } from 'shared/helpers/utils';
import { DownloadOutlined, StopOutlined, UploadOutlined } from '@ant-design/icons';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import CommonTable from 'features/tables/commonTable';
import { TCommonColumns, TOvertimeGetResponse } from 'shared/types/overtime.types';
import { useNavigate, useParams } from 'react-router-dom';
import { OvertimeStatuses, OvertimeStatusesBack } from 'shared/enums/overtime';
import {
  useLazyGetOvertimeByIdQuery,
  useLazyGetOvertimeUsersQuery,
  useLoadOvertimeMutation,
  useUpdateOvertimeUserMutation,
} from 'app/services/edpApiV1/overtime';
import { downloadExcellOvertime } from 'shared/helpers/excell/excell';
import EmployeeInput from 'features/absence/madalAddOneAbsence/employeeInput';
import { TDivisionEmployees } from 'shared/types/types';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { useLazyGetEmployeesQuery } from 'app/services/edpApiV1/employees';
import { useNotifications } from 'shared/hooks/use-notifications';
import { checkOvertimeCompensationTypeToBack } from 'shared/helpers/type-check';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppDispatch, useAppSelector } from 'app/store';
import { viewerModel } from 'entities/viewer';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { uiModel } from 'entities/ui';
import { RvdTabs } from 'features/rvd';
import BadgeData from '../overtimeArchive/bageData/BadgeData';
import style from './index.module.scss';

const FORM_ITEM_NAME = 'loadUpdate';

const LoadOvertime = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [getEmployees, { data: employees }] = useLazyGetEmployeesQuery();
  const dispatch = useAppDispatch();
  const [getOvertimes, { data: overtimeUserResponse, isFetching: isUserFetching, isLoading: isUserLoading }] =
    useLazyGetOvertimeUsersQuery();
  const [getOvertimeById, { data, isLoading, isFetching }] = useLazyGetOvertimeByIdQuery();

  const [isLoadAllowed, setIsLoadAllowed] = useState<boolean>(false);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const currentOrganizationid = useAppSelector(viewerModel.me.selectors.getMeOrganizationId);

  const [signatory, setSignatory] = useState<TDivisionEmployees | null>(null);
  const [getSignatories, { data: signatories }] = useLazyGetDivisionEmployeesQuery();
  const [
    loadOvertimeQuery,
    { isSuccess: isOvertimeLoaded, isLoading: isOvertimeloading, isError: isOvertimeRejected, reset },
  ] = useLoadOvertimeMutation();

  const navigate = useNavigate();
  const [updateUserQuery, { isLoading: isUpdateUserLoading }] = useUpdateOvertimeUserMutation();

  const downloadLink = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    getOvertimeById({ id: id ? +id : 0 });
  }, []);

  useEffect(() => {
    getOvertimes({ size: currentSize, page: currentPage, overtimeId: id ? +id : 0 });
  }, [currentPage, currentSize, getOvertimes, id]);

  useEffect(() => {
    if (employees?.data) {
      const [user] = employees.data;
      form.setFieldValue(FORM_ITEM_NAME, user.id);
      setSignatory({ ...user, division: user.division?.name || '', position: '' });
      getSignatories({
        page: 1,
        size: 10,
        search: `${user.lastName} ${user.name} ${user.middleName}`,
        divisionId: user.division?.divisionId,
        allAvailableOrganizations,
      });
    }
  }, [employees]);

  useEffect(() => {
    if (data) {
      getEmployees({
        isActive: true,
        page: 1,
        size: 1,
        personnelNumber: data.docSignatory,
        allAvailableOrganizations,
        organizationId: currentOrganizationid,
      });
    }
  }, [data, allAvailableOrganizations]);

  useNotifications(
    isOvertimeLoaded,
    isOvertimeRejected,
    'Заявка выгружена в 1С',
    'Заявка не выгружена в 1С',
    reset,
    'topRight',
  );

  const breadCrumbs = [
    { name: 'Сверхурочные', link: '/overtime' },
    { name: 'Выгрузка в 1С', link: `/overtime/load/${id}` },
  ];

  const columns: TCommonColumns = [
    {
      title: 'Дата работ',
      dataIndex: 'toDate',
      key: 'toDate',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Табельный номер',
      dataIndex: 'personnelNumber',
      key: 'personnelNumber',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Время работы',
      dataIndex: 'workDuration',
      key: 'workDuration',
    },
    {
      title: 'Способ компенсации',
      dataIndex: 'type',
      key: 'type',
    },
  ];

  const download = () => {
    downloadExcellOvertime(data);
  };

  const loadToCproduct = async () => {
    try {
      await loadOvertimeQuery({ overtimeId: data?.id || -1 }).unwrap();
      dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
      navigate('/overtime');
    } catch (e: any) {
      console.error(e);
    }
  };

  const cancelLoad = () => {
    dispatch(uiModel.Tabs.actions.setActive(RvdTabs.my));
    navigate('/overtime');
  };

  useEffect(() => {
    if (data?.employees) {
      let isUserWrong = false;
      for (const user of data?.employees) {
        if (user.comment) {
          setIsLoadAllowed(false);
          isUserWrong = true;
        }
      }
      if (!isUserWrong) {
        setIsLoadAllowed(true);
      }
    }
  }, [data?.employees]);

  const onRefreshError = async () => {
    if (!data?.employees) {
      return;
    }
    try {
      for (const user of data?.employees) {
        if (user.comment) {
          await updateUserQuery({
            employeeId: user.userInfo.id,
            overtimeId: Number(id),
            endTime: user.endTime,
            id: user.id!,
            startTime: user.startTime,
            toDate: user.toDate,
            type: checkOvertimeCompensationTypeToBack(user.type),
          });
        }
      }
      notification.success({ message: 'Обновлено!' });
      await getOvertimeById({ id: id ? +id : 0 });
      await getOvertimes({ size: currentSize, page: currentPage, overtimeId: id ? +id : 0 });
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Ошибка обновления') });
    }
  };

  return (
    <>
      <div className={style['top-side']}>
        <h3 className={style.title}>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <div className={style.buttons}>
          <Button onClick={cancelLoad}>
            <StopOutlined /> Отмена
          </Button>
          <Button type="primary" onClick={download}>
            <DownloadOutlined /> Скачать
            <a href="#" ref={downloadLink} style={{ display: 'none' }}></a>
          </Button>
          {data?.status !== OvertimeStatuses.LOADED_CPRODUCT ? (
            <Button
              onClick={loadToCproduct}
              disabled={!isLoadAllowed || data?.status !== OvertimeStatusesBack.NOT_LOADED_CPRODUCT}
              loading={isOvertimeloading}
            >
              <UploadOutlined /> Выгрузить в 1С
            </Button>
          ) : null}
        </div>
      </div>
      <div className={style.statusData}>
        {data?.docNumber && (
          <p>
            <span>№ документа в 1С:</span>
            <span>{data?.docNumber}</span>
          </p>
        )}
        <p>
          <span>Статус документа:</span>
          <span>
            <BadgeData overtime={data as TOvertimeGetResponse} />
          </span>
        </p>
      </div>
      <Spin spinning={isFetching || isLoading || isUserFetching || isUserLoading}>
        <CommonTable
          dataSource={dataForOvertimeUsersTable(overtimeUserResponse?.data ?? [])}
          columns={columns}
          currentPage={currentPage}
          currentSize={currentSize}
          setCurrentPage={setCurrentPage}
          setCurrentSize={setCurrentSize}
          total={overtimeUserResponse?.pagination.total}
        />
      </Spin>
      <Form
        form={form}
        name="basic"
        layout="vertical"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={{ remember: true }}
        onFinish={() => {}}
        autoComplete="off"
      >
        <EmployeeInput
          data={signatories}
          getEmployees={getSignatories}
          setEmployee={setSignatory}
          divisionName={signatory?.division}
          label="Руководитель"
          className="form-item-overtime"
          onChangeEmployee={() => {}}
          formItemName={FORM_ITEM_NAME}
          disabled={true}
        />
      </Form>
      {data?.userComment && (
        <div>
          <p>
            <span>Комментарий: </span>
            <span>{data?.userComment}</span>
          </p>
        </div>
      )}
      {!isLoadAllowed && (
        <div className={style.error}>
          <p>Ошибка: </p>
          <Button onClick={onRefreshError} loading={isUpdateUserLoading}>
            Проверить заявку
          </Button>
        </div>
      )}
      {!isLoadAllowed &&
        data?.employees.map(user => {
          if (user.comment) {
            return <p>{user.comment}</p>;
          }
          return null;
        })}
    </>
  );
};

export default LoadOvertime;
